import { logbookClassFor } from '@/features/aircraft/classes';
import { OVERLAP_THRESHOLD, overlapFraction } from '@/features/import/duplicates';
import { DEFAULT_SETTINGS, SETTINGS_KEYS } from '@/features/settings';
import type { Aircraft, Flight, IsoInstant, LogbookEntry, Track, TrackPoints } from '@/lib/db';
import {
  aircraft as defaultAircraftRepo,
  flights as defaultFlightRepo,
  logbookEntries as defaultLogbookRepo,
  settings as defaultSettingsRepo,
  tracks as defaultTrackRepo,
} from '@/lib/db';
import type {
  AircraftRepository,
  FlightRepository,
  LogbookRepository,
  SettingsRepository,
  TrackRepository,
} from '@/lib/db';
import { formatLocalDate } from '@/lib/format';

import { aircraftTypeLabel, trackDerivedValues } from './derived-values';
import { blankApproaches, blankTimeMatrix, markDerived } from './entry';

/**
 * Draft-and-approve on import — `PLAN.md` §6 Phase 5.
 *
 * A successful track import writes a `Flight` and a draft `LogbookEntry`
 * automatically, pre-filled from the track, unless a matching entry already
 * exists — same dedup shape as `features/import/duplicates.ts`: same
 * aircraft, same local day, and either no track on the existing entry (a
 * hand-written entry already covers it) or a genuinely overlapping one.
 * Re-importing a file never spawns a second draft; two real flights on the
 * same aircraft and day each still get their own.
 */

export type DraftOutcome = 'drafted' | 'existing';

export interface DraftResult {
  outcome: DraftOutcome;
  flight: Flight;
  entry: LogbookEntry;
}

export interface DraftDeps {
  aircraftRepo?: AircraftRepository;
  flightRepo?: FlightRepository;
  logbookRepo?: LogbookRepository;
  settingsRepo?: SettingsRepository;
  trackRepo?: TrackRepository;
  now?: () => string;
}

/** True once a pilot has typed over `field` themselves — `applyCellEdit` always logs a history entry for a real change, so an untouched field never has one. */
function hasManualEdit(entry: LogbookEntry, field: string): boolean {
  return entry.history.some((edit) => edit.field === field);
}

/**
 * Fills `aircraftType` and the engine-class column — which of the NZ-style
 * SE/ME/etc hour columns (`classHoursColumn` in `columns.ts`) an entry's time
 * lands under — from a matched `Aircraft` record. Called the moment a pilot
 * types a registration the app recognises, so the class is set automatically
 * rather than being another cell to fill in by hand, per `NZ-LOGBOOK.md` §3's
 * engine/class split.
 *
 * Never overwrites a value the pilot has actually typed over: that's tracked
 * the same way any other cell edit is, through a `history` entry for the
 * field, so a manual correction sticks even if the entry is later re-matched
 * to the same or a different aircraft.
 */
export function applyAircraftMatch(
  entry: LogbookEntry,
  aircraft: Aircraft | undefined,
  now: IsoInstant = new Date().toISOString(),
): LogbookEntry {
  if (aircraft === undefined) {
    return entry;
  }
  const typeLabel = aircraftTypeLabel(aircraft);
  const fillType =
    typeLabel !== undefined &&
    !hasManualEdit(entry, 'aircraftType') &&
    entry.aircraftType !== typeLabel;
  const fillClass =
    !hasManualEdit(entry, 'time.engineClass') &&
    entry.time.engineClass !== logbookClassFor(aircraft);
  const linkAircraft = entry.aircraftId !== aircraft.id;

  if (!fillType && !fillClass && !linkAircraft) {
    return entry;
  }

  const derivedFields = new Set(entry.derivedFields);
  if (fillClass) {
    derivedFields.add('time.engineClass');
  }
  if (fillType) {
    derivedFields.add('aircraftType');
  }

  return {
    ...entry,
    aircraftId: aircraft.id,
    ...(fillType ? { aircraftType: typeLabel } : {}),
    time: fillClass ? { ...entry.time, engineClass: logbookClassFor(aircraft) } : entry.time,
    derivedFields: [...derivedFields],
    updatedAt: now,
  };
}

async function findExistingMatch(
  track: Track,
  candidates: readonly LogbookEntry[],
  flightRepo: FlightRepository,
  trackRepo: TrackRepository,
): Promise<LogbookEntry | undefined> {
  for (const candidate of candidates) {
    const candidateFlight = await flightRepo.get(candidate.flightId);
    if (candidateFlight?.trackId === undefined) {
      // A hand-written entry already sits on this aircraft and day.
      return candidate;
    }
    const candidateTrack = await trackRepo.get(candidateFlight.trackId);
    if (candidateTrack === undefined) {
      continue;
    }
    const overlap = overlapFraction(
      track.startTime,
      track.endTime,
      candidateTrack.startTime,
      candidateTrack.endTime,
    );
    if (overlap >= OVERLAP_THRESHOLD) {
      return candidate;
    }
  }
  return undefined;
}

export async function draftLogbookEntryForTrack(
  track: Track,
  points: TrackPoints,
  deps: DraftDeps = {},
): Promise<DraftResult> {
  const aircraftRepo = deps.aircraftRepo ?? defaultAircraftRepo;
  const flightRepo = deps.flightRepo ?? defaultFlightRepo;
  const logbookRepo = deps.logbookRepo ?? defaultLogbookRepo;
  const settingsRepo = deps.settingsRepo ?? defaultSettingsRepo;
  const trackRepo = deps.trackRepo ?? defaultTrackRepo;
  const now = deps.now ?? (() => new Date().toISOString());

  const timeZone = await settingsRepo.get(SETTINGS_KEYS.timeZone, DEFAULT_SETTINGS.timeZone);
  const date = formatLocalDate(track.startTime, { format: 'iso', timeZone });

  const aircraft =
    track.registration === undefined
      ? undefined
      : await aircraftRepo.findByRegistration(track.registration);

  const candidates =
    aircraft === undefined
      ? (await logbookRepo.listOrdered()).filter(
          (entry) => entry.date === date && entry.registration === track.registration,
        )
      : await logbookRepo.listByAircraftAndDate(aircraft.id, date);

  const existing = await findExistingMatch(track, candidates, flightRepo, trackRepo);
  if (existing !== undefined) {
    const existingFlight = await flightRepo.get(existing.flightId);
    return {
      outcome: 'existing',
      entry: existing,
      flight: existingFlight ?? {
        id: existing.flightId,
        date,
        derived: { confidence: 'low' },
        createdAt: existing.createdAt,
        updatedAt: existing.updatedAt,
      },
    };
  }

  const timestamp = now();
  const values = trackDerivedValues(track, points, aircraft);

  const flight: Flight = {
    id: crypto.randomUUID(),
    trackId: track.id,
    date,
    ...(aircraft === undefined ? {} : { aircraftId: aircraft.id }),
    ...(values.departure === undefined ? {} : { departure: values.departure }),
    ...(values.arrival === undefined ? {} : { arrival: values.arrival }),
    derived: values.derived,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
  await flightRepo.put(flight);

  const entry: LogbookEntry = markDerived(
    {
      id: crypto.randomUUID(),
      flightId: flight.id,
      date,
      ...(aircraft === undefined ? {} : { aircraftId: aircraft.id }),
      ...(values.registration === undefined ? {} : { registration: values.registration }),
      ...(values.aircraftType === undefined ? {} : { aircraftType: values.aircraftType }),
      pic: 'SELF',
      ...(values.from === undefined ? {} : { from: values.from }),
      ...(values.to === undefined ? {} : { to: values.to }),
      time: {
        ...blankTimeMatrix(
          aircraft === undefined ? 'single-engine-land' : logbookClassFor(aircraft),
        ),
        dayHr: values.dayHr,
        nightHr: values.nightHr,
        picHr: Math.round((values.dayHr + values.nightHr) * 100) / 100,
      },
      approaches: blankApproaches(),
      landings: { ...values.landings },
      takeoffs: { ...values.takeoffs },
      isDraft: true,
      derivedFields: [],
      history: [],
      createdAt: timestamp,
      updatedAt: timestamp,
    },
    values.derivedFields,
  );
  await logbookRepo.put(entry);

  return { outcome: 'drafted', flight, entry };
}

/**
 * Links a newly-added (or edited) aircraft record onto whatever drafts were
 * written before it existed. `draftLogbookEntryForTrack` only fills in
 * `aircraftId`/`aircraftType` when the aircraft is already on file at import
 * time — a track imported first and registered second used to leave its
 * draft permanently blank on both. A draft still in review is fair game to
 * touch; a confirmed entry is a pilot's record and is left alone even if it
 * happens to share the registration.
 */
export async function backfillDraftsForAircraft(
  aircraft: Aircraft,
  deps: DraftDeps = {},
): Promise<LogbookEntry[]> {
  const flightRepo = deps.flightRepo ?? defaultFlightRepo;
  const logbookRepo = deps.logbookRepo ?? defaultLogbookRepo;
  const now = deps.now ?? (() => new Date().toISOString());

  const candidates = (await logbookRepo.listOrdered()).filter(
    (entry) =>
      entry.isDraft &&
      entry.aircraftId === undefined &&
      entry.registration === aircraft.registration,
  );

  const type = aircraftTypeLabel(aircraft);
  const updated: LogbookEntry[] = [];

  for (const candidate of candidates) {
    const timestamp = now();
    const fillType = type !== undefined && candidate.aircraftType === undefined;
    // Unlike `aircraftType` above (only filled when blank), the class always
    // holds *some* value — `single-engine-land` by default — so "blank" isn't
    // a usable signal here. A manual edit is: `applyAircraftMatch`'s own rule.
    const fillClass =
      !hasManualEdit(candidate, 'time.engineClass') &&
      candidate.time.engineClass !== logbookClassFor(aircraft);
    const derivedFields = new Set(candidate.derivedFields);
    if (fillType) {
      derivedFields.add('aircraftType');
    }
    if (fillClass) {
      derivedFields.add('time.engineClass');
    }

    const entry: LogbookEntry = {
      ...candidate,
      aircraftId: aircraft.id,
      ...(fillType ? { aircraftType: type } : {}),
      time: fillClass
        ? { ...candidate.time, engineClass: logbookClassFor(aircraft) }
        : candidate.time,
      derivedFields: [...derivedFields],
      updatedAt: timestamp,
    };
    await logbookRepo.put(entry);
    updated.push(entry);

    const flight = await flightRepo.get(entry.flightId);
    if (flight !== undefined && flight.aircraftId === undefined) {
      await flightRepo.put({ ...flight, aircraftId: aircraft.id, updatedAt: timestamp });
    }
  }

  return updated;
}
